/**
 * 红色商务模板 - 列表页（v3）
 * 
 * 设计核心理念：专业数据目录风格
 * - 面包屑导航 + 左侧分类筛选 + 右侧数据表格
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/templates/red/components/Navbar";
import Footer from "@/templates/red/components/Footer";
import {
  getSiteCategories,
  getListings,
  getNavCategories,
  type FrontendCategory,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";

const SORT_OPTIONS = [
  { key: "latest", label: "最新发布", icon: "ri-time-line" },
  { key: "popular", label: "最多浏览", icon: "ri-fire-line" },
  { key: "alpha", label: "按名称", icon: "ri-sort-alphabet-asc" },
] as const;

export default function RedCategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { siteId, loading: siteLoading } = useSite();

  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getSiteCategories().then(setCategories).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (slug && categories.length > 0) {
      const found = categories.find((c) => c.slug === slug || c.id === slug);
      if (found) setActiveCategoryId(found.id);
    }
  }, [slug, categories]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    setLoading(true);
    const params: Record<string, any> = { page: currentPage, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    getListings(params)
      .then((data) => {
        setListings(data.list || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId, siteLoading, activeCategoryId, currentPage]);

  const currentCategoryName = useMemo(() => {
    if (activeCategoryId) {
      const cat = categories.find((c) => c.id === activeCategoryId);
      return cat?.name || "全部分类";
    }
    return "全部分类";
  }, [activeCategoryId, categories]);

  const sortedListings = useMemo(() => {
    const list = [...listings];
    if (sortBy === "popular") list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    if (sortBy === "alpha") list.sort((a, b) => a.title.localeCompare(b.title, "zh"));
    return list;
  }, [listings, sortBy]);

  const displayCategories = navCategories.length > 0 ? navCategories : categories;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Navbar />

      {/* pt-20 给固定导航栏让位，面包屑 */}
      <div className="bg-white border-b border-gray-200 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <nav className="flex items-center gap-1.5 text-sm">
            <Link to="/" className="text-gray-400 hover:text-red-600 transition-colors flex items-center gap-1">
              <i className="ri-home-4-line" /> 首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs text-gray-300" />
            {activeCategoryId ? (
              <>
                <Link to="/category" className="text-gray-400 hover:text-red-600 transition-colors">全部分类</Link>
                <i className="ri-arrow-right-s-line text-xs text-gray-300" />
                <span className="text-red-600 font-semibold">{currentCategoryName}</span>
              </>
            ) : (
              <span className="text-red-600 font-semibold">全部分类</span>
            )}
            <span className="ml-2 text-xs text-gray-400">共 {total} 条</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* ── 左侧边栏：分类筛选 ── */}
          <aside className="w-full lg:w-52 shrink-0">
            <div className="lg:sticky lg:top-20 space-y-3">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
                  <h3 className="text-sm font-bold text-gray-700 flex items-center gap-1.5">
                    <i className="ri-filter-3-line text-red-500" />
                    分类筛选
                  </h3>
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                  <button
                    onClick={() => { setActiveCategoryId(""); setCurrentPage(1); }}
                    className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors cursor-pointer ${
                      !activeCategoryId
                        ? "bg-red-50 text-red-700 border-l-2 border-red-600 font-semibold"
                        : "text-gray-600 hover:bg-gray-50 border-l-2 border-transparent"
                    }`}
                  >
                    <i className="ri-global-line text-xs" />
                    <span>全部分类</span>
                    <span className={`ml-auto text-xs px-1.5 py-0.5 rounded ${
                      !activeCategoryId ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-400"
                    }`}>{total}</span>
                  </button>

                  {displayCategories.slice(0, 15).map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setActiveCategoryId(cat.id === activeCategoryId ? "" : cat.id);
                        setCurrentPage(1);
                      }}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors cursor-pointer ${
                        activeCategoryId === cat.id
                          ? "bg-red-50 text-red-700 border-l-2 border-red-600 font-semibold"
                          : "text-gray-600 hover:bg-gray-50 border-l-2 border-transparent"
                      }`}
                    >
                      <i className={`${cat.icon || "ri-folder-line"} text-xs`} />
                      <span className="truncate">{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <Link
                to="/publish"
                className="block bg-red-600 rounded-lg p-3 text-center hover:bg-red-700 transition-colors"
              >
                <i className="ri-add-line text-white text-xl block mb-1" />
                <span className="text-white text-xs font-semibold">发布信息</span>
              </Link>
            </div>
          </aside>

          {/* ── 右侧主内容 ── */}
          <div className="flex-1 min-w-0">
            {/* 工具栏 */}
            <div className="flex items-center justify-between mb-4 bg-white rounded-lg border border-gray-200 px-4 py-2.5">
              <h2 className="text-sm font-bold text-gray-700">{currentCategoryName}</h2>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer ${
                      sortBy === opt.key ? "bg-white text-red-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <i className={`${opt.icon} text-[11px]`} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── 数据表格 ── */}
            {loading ? (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-6 space-y-3">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="animate-pulse flex items-center gap-4 py-3 border-b border-gray-50 last:border-0">
                      <div className="w-12 h-12 bg-gray-200 rounded shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="w-2/3 h-5 bg-gray-200 rounded" />
                        <div className="w-1/3 h-3.5 bg-gray-100 rounded" />
                      </div>
                      <div className="w-16 h-3.5 bg-gray-100 rounded hidden sm:block" />
                      <div className="w-14 h-3.5 bg-gray-100 rounded hidden md:block" />
                    </div>
                  ))}
                </div>
              </div>
            ) : sortedListings.length > 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* 桌面表格 */}
                <table className="w-full hidden md:table">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-2.5 w-10">#</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-2.5">信息标题</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-2.5 w-24">分类</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-2.5 w-20">联系人</th>
                      <th className="text-left text-xs text-gray-400 font-medium px-4 py-2.5 w-24">
                        {sortBy === "popular" ? "浏览" : "发布时间"}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {sortedListings.map((listing, index) => (
                      <tr
                        key={listing.id}
                        className={`group hover:bg-red-50/30 transition-colors cursor-pointer ${
                          listing.is_pinned ? "bg-amber-50/30" : ""
                        }`}
                        onClick={() => {
                          window.location.href = `/listing/${listing.id}`;
                        }}
                      >
                        <td className="px-4 py-3.5">
                          {listing.is_pinned ? (
                            <span className="w-7 h-7 bg-red-600 text-white text-[10px] font-bold rounded flex items-center justify-center">顶</span>
                          ) : (
                            <span className="text-xs text-gray-400 pl-1">{(currentPage - 1) * PAGE_SIZE + index + 1}</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                              {listing.images?.[0] ? (
                                <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <i className="ri-image-line text-gray-300 text-xl" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <Link
                                to={`/listing/${listing.id}`}
                                className="text-sm font-medium text-gray-800 group-hover:text-red-700 transition-colors truncate block max-w-xs lg:max-w-lg"
                              >
                                {listing.title}
                              </Link>
                              <p className="text-xs text-gray-400 truncate max-w-xs lg:max-w-lg mt-0.5">
                                {listing.content?.substring(0, 80) || "暂无描述"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded whitespace-nowrap">
                            {listing.category?.name || "未分类"}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-xs text-gray-500 whitespace-nowrap">{listing.contact_name || "匿名"}</td>
                        <td className="px-4 py-3.5 text-xs text-gray-400 whitespace-nowrap">
                          {sortBy === "popular" ? `${listing.view_count} 次` : formatTableDate(listing.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* 移动端列表 */}
                <div className="md:hidden divide-y divide-gray-50">
                  {sortedListings.map((listing) => (
                    <Link
                      key={listing.id}
                      to={`/listing/${listing.id}`}
                      className={`flex items-start gap-3 px-4 py-3.5 hover:bg-red-50/20 transition-colors block ${
                        listing.is_pinned ? "bg-amber-50/20" : ""
                      }`}
                    >
                      <div className="flex items-start gap-3 w-full">
                        <div className="w-14 h-14 rounded bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                          {listing.images?.[0] ? (
                            <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <i className="ri-image-line text-gray-300 text-xl" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            {listing.is_pinned && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-red-600 text-white rounded font-bold shrink-0">顶</span>
                            )}
                            <span className="text-sm font-medium text-gray-800 truncate">{listing.title}</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-1 line-clamp-1">{listing.content?.substring(0, 60) || "暂无描述"}</p>
                          <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-gray-400 flex-wrap">
                            <span>{listing.category?.name || "未分类"}</span>
                            <span>·</span>
                            <span>{listing.contact_name || "匿名"}</span>
                            <span>·</span>
                            <span>{formatTableDate(listing.created_at)}</span>
                            {sortBy === "popular" && <><span>·</span><span className="text-red-400">{listing.view_count} 浏览</span></>}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/30 text-xs text-gray-400">
                  第 {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, total)} 条，共 {total} 条
                </div>
              </div>
            ) : (
              <div className="text-center py-14 bg-white rounded-lg border border-gray-200">
                <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-gray-100">
                  <i className="ri-inbox-line text-xl text-gray-400" />
                </div>
                <p className="text-gray-500 font-medium">该分类暂无信息</p>
                <p className="text-gray-400 text-sm mt-1">换个分类试试</p>
              </div>
            )}

            {/* 分页 */}
            {!loading && totalPages > 1 && (
              <div className="mt-5 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:border-red-300 hover:text-red-600 disabled:opacity-30 transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>

                {generatePageNumbers(currentPage, totalPages).map((p, i) =>
                  typeof p === "number" ? (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(p)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all cursor-pointer ${
                        currentPage === p ? "bg-red-600 text-white" : "border border-gray-200 bg-white text-gray-600 hover:border-red-300"
                      }`}
                    >
                      {p}
                    </button>
                  ) : (
                    <span key={i} className="w-9 h-9 flex items-center justify-center text-gray-400 text-xs">...</span>
                  )
                )}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:border-red-300 hover:text-red-600 disabled:opacity-30 transition-all cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function formatTableDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}

function generatePageNumbers(current: number, total: number): (number | string)[] {
  const pages: (number | string)[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push("...");
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      pages.push(i);
    }
    if (current < total - 2) pages.push("...");
    pages.push(total);
  }
  return pages;
}
